import { PaginatedContentConfig } from '@order-management/types';
import { ReactNode, useCallback, useState } from 'react';
import { Form } from 'react-bulma-components';
import { useDebouncedCallback } from 'use-debounce';
import If from '../../../shared/if/if';
import Paginator from '../../paginator/paginator';
import SearchBar from '../../search-bar/search-bar';

export type OnTableFormChangeFunction = (value: PaginatedContentConfig) => void;
export type TableFormConfig = PaginatedContentConfig;

export type TableFormProps = {
  config: TableFormConfig;
  onChange: OnTableFormChangeFunction;
  leftExtra?: ReactNode;
  rightExtra?: ReactNode;
  enableSearch?: boolean;
  disableItemsPerPageSelection?: boolean;
  debounceMillis?: number;
  pagesDisplayLimit?: number;
  itemsPerPageOptions?: number[];
};

export function TableForm({
  config,
  onChange,
  leftExtra,
  rightExtra,
  enableSearch = true,
  debounceMillis = 1000,
  itemsPerPageOptions,
  disableItemsPerPageSelection,
}: TableFormProps) {
  const [search, setSearch] = useState(config.search);

  const handleSeachInputChange = useDebouncedCallback((value: string) => {
    setSearch(value);
    onChange({ ...config, search: value });
  });

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: number) => {
      onChange({ ...config, itemsPerPage });
    },
    [config, onChange]
  );

  const handlePageNumberChange = useCallback(
    (pageNumber: number) => {
      onChange({ ...config, pageNumber });
    },
    [config, onChange]
  );

  return (
    <form
      role="search"
      className="w-full is-flex is-justify-content-space-between py-4 px-2"
    >
      <Form.Field className="is-flex is-align-items-center is-marginless">
        {leftExtra}
      </Form.Field>

      <Form.Field className="is-flex is-align-items-center gap-2">
        {rightExtra}
        <If condition={enableSearch}>
          <SearchBar
            onChange={handleSeachInputChange}
            searchValue={search}
            debounceMillis={debounceMillis}
          />
        </If>
        <Paginator
          config={config}
          onItemsPerPageChange={handleItemsPerPageChange}
          onPageNumberChange={handlePageNumberChange}
          itemsPerPageOptions={itemsPerPageOptions}
          disableItemsPerPageSelection={disableItemsPerPageSelection}
        />
      </Form.Field>
    </form>
  );
}

export default TableForm;
